"use client";
import { useState } from "react";
import type { Control, Path, PathValue, UseFormSetValue } from "react-hook-form";
import { Autocomplete } from "./Autocomplete";
import { Dropdown } from "./Dropdown";
import { Input } from "./Input";

type AddressFields = {
  address1: string;
  address2?: string;
  zip: string;
  city: string;
  countryId: string;
};

type Props<T extends AddressFields> = {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  countries: {
    id: string;
    label: string;
  }[];
  googleMapsApiKey: string;
  inverse?: boolean;
};
export const AddressInput = <T extends AddressFields>({
  control,
  setValue,
  countries,
  googleMapsApiKey,
  inverse = false,
}: Props<T>) => {
  const [showFullAddressForm, setShowFullAddressForm] = useState(false);

  return (
    <>
      {!showFullAddressForm && (
        <div className="flex flex-col gap-1">
          <Autocomplete
            googleMapsApiKey={googleMapsApiKey}
            onSelect={(result) => {
              setValue("address1" as Path<T>, (result.addressLine1 || "") as PathValue<T, Path<T>>);
              setValue("zip" as Path<T>, (result.postalCode || "") as PathValue<T, Path<T>>);
              setValue("city" as Path<T>, (result.city || "") as PathValue<T, Path<T>>);
              setValue("countryId" as Path<T>, (result.country || "") as PathValue<T, Path<T>>);
              setShowFullAddressForm(true);
            }}
            inverse={inverse}
          />
          <p
            onClick={() => setShowFullAddressForm(true)}
            className="text-xs/[14px] pl-2 cursor-pointer text-tx-2 hover:text-pr-1"
          >
            Saisir manuellement
          </p>
        </div>
      )}
      {showFullAddressForm && (
        <>
          <Input
            id="address-line1"
            label="Adresse ligne 1*"
            name={"address1" as Path<T>}
            control={control}
            placeholder="Rue, voie, boite postale"
            inverse={inverse}
          />
          <Input
            id="address-line2"
            label="Adresse ligne 2"
            name={"address2" as Path<T>}
            control={control}
            placeholder="Appartement, bâtiment, étage, code d'accès"
            inverse={inverse}
          />
          <div className="grid gap-4 grid-cols-3">
            <Input id="postal-code" label="Code postal*" name={"zip" as Path<T>} control={control} inverse={inverse} />
            <Input
              id="address-level2"
              label="Ville*"
              name={"city" as Path<T>}
              control={control}
              className="col-span-2"
              inverse={inverse}
            />
          </div>
          <Dropdown
            id="country"
            label="Pays *"
            name={"countryId" as Path<T>}
            control={control}
            items={countries.map((c) => ({
              text: c.label,
              callbackValue: c.id,
            }))}
            inverse={inverse}
          />
        </>
      )}
    </>
  );
};
